import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Spinner } from "@/components/kit";
import { api, errMsg } from "@/lib/api";
import { useWs } from "@/context/WorkspaceContext";
import Empresa from "@/pages/Empresa";
import { BizCtx, ROLE_LABEL, monthNow } from "./shared";
import { Overview } from "./Overview";
import { Companies } from "./Companies";
import { Entries } from "./Entries";
import { Indicators } from "./Indicators";
import { Simulator } from "./Simulator";
import { Diagnosis, ActionPlan } from "./AI";
import { Evolution } from "./Evolution";
import { Consultant } from "./Consultant";
import { Reports, Settings } from "./ReportsSettings";
import { Compare } from "./Compare";

const TABS = [["visao", "Visão Geral", Overview], ["empresas", "Minhas Empresas", Companies], ["lancamentos", "Lançamentos", Entries], ["indicadores", "Nove Indicadores", Indicators],
  ["simulador", "Simulador", Simulator], ["diagnostico", "Diagnóstico com IA", Diagnosis], ["plano", "Plano de Ação", ActionPlan], ["evolucao", "Evolução Mensal", Evolution],
  ["consultor", "Consultor IA", Consultant], ["relatorios", "Relatórios", Reports], ["config", "Configurações", Settings], ["comparar", "Comparar empresas", Compare], ["estimativa", "Estimativa rápida", Empresa]];

export default function BizModule() {
  const [access, setAccess] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [cid, setCid] = useState(localStorage.getItem("pp_biz_cid") || "");
  const [month, setMonth] = useState(monthNow());
  const [tab, setTab] = useState("visao");
  const { wid } = useWs();
  const reloadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/biz/companies");
      setCompanies(data);
      setCid((c) => (data.some((x) => x.id === c) ? c : data[0]?.id || ""));
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { api.get("/biz/access").then((r) => setAccess(r.data)); reloadCompanies(); }, [reloadCompanies]);
  useEffect(() => { if (cid) { localStorage.setItem("pp_biz_cid", cid); } }, [cid]);
  const lastWid = useRef(null);
  useEffect(() => {
    if (!companies || wid === lastWid.current) { return; }
    const first = lastWid.current === null;
    lastWid.current = wid;
    const linked = companies.find((c) => c.workspace_id && c.workspace_id === wid);
    if (linked && !(first && companies.some((c) => c.id === localStorage.getItem("pp_biz_cid")))) { setCid(linked.id); }
  }, [wid, companies]);
  if (!access || !companies) return <Spinner />;
  const company = companies.find((c) => c.id === cid);
  const header = <PageHeader eyebrow="Empresas" title="Diagnóstico Empresarial" subtitle="Entenda os números da sua empresa, identifique oportunidades e tome decisões inteligentes para prosperar." />;
  if (!access.plan_ok && !companies.length) {
    return (
      <div data-testid="biz-module">{header}
        <div className="card-p max-w-xl p-8" data-testid="biz-upgrade">
          <Lock className="h-8 w-8 text-[#D4AF37]" />
          <h3 className="mt-3 text-xl font-semibold">Disponível nos planos Empresarial e Premium</h3>
          <p className="mt-2 text-sm text-slate-400">Cadastre suas empresas, acompanhe os nove indicadores, simule cenários e receba diagnósticos com IA.</p>
          <Button asChild className="mt-5" data-testid="biz-upgrade-btn"><Link to="/app/assinatura">Ver planos</Link></Button>
        </div>
      </div>
    );
  }
  const ctx = { cid, company, companies, reloadCompanies, setCid, month, setMonth, role: company?.my_role, plan_ok: access.plan_ok, compareOk: access.compare_ok, goTab: setTab };
  const tabs = TABS.filter(([k]) => (k !== "estimativa" || wid) && (k !== "comparar" || companies.length > 1));
  return (
    <BizCtx.Provider value={ctx}>
      <div data-testid="biz-module">
        {header}
        <div className="mb-6 flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-400">Empresa
            <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="biz-company-select"
              className="mt-1 block h-10 min-w-[220px] rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-3 text-sm text-white">
              {!companies.length && <option value="">Nenhuma empresa cadastrada</option>}
              {companies.map((c) => <option key={c.id} value={c.id}>{c.trade_name}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-400">Mês de referência
            <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="biz-month-input"
              className="mt-1 block h-10 rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-3 text-sm text-white [color-scheme:dark]" />
          </label>
          {company && <span className="pb-2 text-xs text-slate-500" data-testid="biz-my-role">Seu perfil: {ROLE_LABEL[company.my_role]}</span>}
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <div className="-mx-1 mb-6 overflow-x-auto pb-1">
            <TabsList className="inline-flex h-auto w-max gap-1 bg-[#0F2242] p-1">
              {tabs.map(([k, label]) => <TabsTrigger key={k} value={k} className="whitespace-nowrap data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B1A33]" data-testid={`biz-tab-${k}`}>{label}</TabsTrigger>)}
            </TabsList>
          </div>
          {tabs.map(([k, , C]) => (
            <TabsContent key={k} value={k}>
              {k === "empresas" || k === "estimativa" || company ? <C /> : (
                <div className="card-p p-8 text-center" data-testid="biz-no-company">
                  <Building2 className="mx-auto h-8 w-8 text-[#D4AF37]" />
                  <p className="mt-3 text-slate-300">Cadastre sua primeira empresa para começar.</p>
                  <Button className="mt-4" onClick={() => setTab("empresas")} data-testid="biz-go-companies">Cadastrar empresa</Button>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </BizCtx.Provider>
  );
}
